import { useMutation } from "@tanstack/react-query";
import { EyeIcon, EyeOffIcon, KeyRoundIcon, LockKeyholeIcon, UserPlusIcon } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import {
  WorkspaceMemberCreateSchema,
  type WorkspaceAdminResponse,
  type WorkspaceMemberCreated,
} from "../../shared/schemas";
import { ApiClientError, createWorkspaceMember } from "../api/client";
import { cn } from "../lib/utils";
import { ToolSelection } from "./WorkspaceToolSelection";
import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";

type AddUserErrors = Partial<
  Record<"username" | "displayName" | "temporaryPassword" | "role" | "toolAccess", string>
>;

function withoutError(errors: AddUserErrors, field: keyof AddUserErrors): AddUserErrors {
  return Object.fromEntries(Object.entries(errors).filter(([key]) => key !== field));
}

function parseErrors(issues: { path: PropertyKey[]; message: string }[]): AddUserErrors {
  const errors: AddUserErrors = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (
      typeof field === "string" &&
      field in WorkspaceMemberCreateSchema.shape &&
      !errors[field as keyof AddUserErrors]
    ) {
      errors[field as keyof AddUserErrors] = issue.message;
    }
  }
  return errors;
}

function useAddUserController(input: {
  organizationId: string;
  onCreated: (result: WorkspaceMemberCreated) => Promise<void> | void;
  onClose: () => void;
}) {
  const [accountType, setAccountType] = useState<"new" | "existing">("new");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState<"member" | "organization_admin">("member");
  const [toolAccess, setToolAccess] = useState<string[]>([]);
  const [errors, setErrors] = useState<AddUserErrors>({});
  function reset() {
    setAccountType("new");
    setUsername("");
    setDisplayName("");
    setTemporaryPassword("");
    setShowPassword(false);
    setRole("member");
    setToolAccess([]);
    setErrors({});
  }
  const mutation = useMutation({
    mutationFn: () =>
      createWorkspaceMember(input.organizationId, {
        accountType,
        username,
        displayName: accountType === "new" ? displayName : null,
        temporaryPassword: accountType === "new" ? temporaryPassword : null,
        role,
        toolAccess: role === "member" ? toolAccess : [],
      }),
    onSuccess: async (result) => {
      await input.onCreated(result);
      input.onClose();
      reset();
    },
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = WorkspaceMemberCreateSchema.safeParse({
      accountType,
      username,
      displayName: accountType === "new" ? displayName : null,
      temporaryPassword: accountType === "new" ? temporaryPassword : null,
      role,
      toolAccess: role === "member" ? toolAccess : [],
    });
    if (!parsed.success) {
      setErrors(parseErrors(parsed.error.issues));
      return;
    }
    setErrors({});
    mutation.mutate();
  }
  return {
    accountType,
    displayName,
    errors,
    mutation,
    reset,
    role,
    setAccountType,
    setDisplayName,
    setErrors,
    setRole,
    setShowPassword,
    setTemporaryPassword,
    setToolAccess,
    setUsername,
    showPassword,
    submit,
    temporaryPassword,
    toolAccess,
    username,
  };
}

type Controller = ReturnType<typeof useAddUserController>;

function AccountTypeTabs({ controller }: { controller: Controller }) {
  return (
    <div
      aria-label="Account type"
      className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1"
      role="tablist"
    >
      {(["new", "existing"] as const).map((value) => (
        <button
          aria-selected={controller.accountType === value}
          className={cn(
            "min-h-10 rounded-sm border border-transparent px-3 text-sm font-semibold text-muted-foreground outline-none transition-[color,background-color,border-color,box-shadow] duration-150 focus-visible:shadow-[var(--focus-ring)]",
            controller.accountType === value &&
              "border-input bg-card text-foreground shadow-button",
          )}
          key={value}
          role="tab"
          type="button"
          onClick={() => {
            controller.setAccountType(value);
            controller.setErrors({});
            controller.mutation.reset();
          }}
        >
          {value === "new" ? "New account" : "Existing account"}
        </button>
      ))}
    </div>
  );
}

function IdentityFields({ controller }: { controller: Controller }) {
  const clear = (field: keyof AddUserErrors) => {
    controller.setErrors((current) => withoutError(current, field));
  };
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {controller.accountType === "new" ? (
        <Field data-invalid={Boolean(controller.errors.displayName)}>
          <FieldLabel htmlFor="workspace-user-name">Full name</FieldLabel>
          <Input
            aria-invalid={Boolean(controller.errors.displayName)}
            autoComplete="off"
            disabled={controller.mutation.isPending}
            id="workspace-user-name"
            maxLength={120}
            placeholder="e.g. Dana Rivera"
            value={controller.displayName}
            onChange={(event) => {
              controller.setDisplayName(event.target.value);
              clear("displayName");
            }}
          />
          <FieldError>{controller.errors.displayName}</FieldError>
        </Field>
      ) : null}
      <Field data-invalid={Boolean(controller.errors.username)}>
        <FieldLabel htmlFor="workspace-user-username">Username or email</FieldLabel>
        <Input
          aria-invalid={Boolean(controller.errors.username)}
          autoCapitalize="none"
          autoComplete="off"
          disabled={controller.mutation.isPending}
          id="workspace-user-username"
          maxLength={64}
          placeholder="e.g. dana@company.com"
          spellCheck={false}
          value={controller.username}
          onChange={(event) => {
            controller.setUsername(event.target.value);
            clear("username");
          }}
        />
        <FieldError>{controller.errors.username}</FieldError>
      </Field>
      {controller.accountType === "new" ? (
        <PasswordField
          controller={controller}
          clearError={() => {
            clear("temporaryPassword");
          }}
        />
      ) : null}
      <Field data-invalid={Boolean(controller.errors.role)}>
        <FieldLabel htmlFor="workspace-user-role">Role</FieldLabel>
        <select
          className="min-h-10 w-full rounded-sm border border-input bg-card px-3 py-[9px] text-sm outline-none transition-[border-color,box-shadow] duration-150 focus-visible:border-primary focus-visible:shadow-[var(--field-focus-shadow)]"
          disabled={controller.mutation.isPending}
          id="workspace-user-role"
          value={controller.role}
          onChange={(event) => {
            const role = WorkspaceMemberCreateSchema.shape.role.parse(event.target.value);
            controller.setRole(role);
            if (role === "organization_admin") controller.setToolAccess([]);
          }}
        >
          <option value="member">Member</option>
          <option value="organization_admin">Workspace administrator</option>
        </select>
        <FieldDescription>
          Administrators can manage users, settings, and every tool.
        </FieldDescription>
        <FieldError>{controller.errors.role}</FieldError>
      </Field>
    </div>
  );
}

function PasswordField({
  controller,
  clearError,
}: {
  controller: Controller;
  clearError: () => void;
}) {
  return (
    <Field data-invalid={Boolean(controller.errors.temporaryPassword)}>
      <FieldLabel htmlFor="workspace-user-password">Temporary password</FieldLabel>
      <div className="relative">
        <Input
          aria-invalid={Boolean(controller.errors.temporaryPassword)}
          autoComplete="new-password"
          className="pr-12"
          disabled={controller.mutation.isPending}
          id="workspace-user-password"
          maxLength={1024}
          placeholder="At least 8 characters"
          type={controller.showPassword ? "text" : "password"}
          value={controller.temporaryPassword}
          onChange={(event) => {
            controller.setTemporaryPassword(event.target.value);
            clearError();
          }}
        />
        <button
          aria-label={
            controller.showPassword ? "Hide temporary password" : "Show temporary password"
          }
          className="absolute inset-y-0 right-0 grid min-w-11 place-items-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:shadow-[var(--focus-ring)]"
          type="button"
          onClick={() => {
            controller.setShowPassword((current) => !current);
          }}
        >
          {controller.showPassword ? (
            <EyeOffIcon aria-hidden="true" className="size-4" />
          ) : (
            <EyeIcon aria-hidden="true" className="size-4" />
          )}
        </button>
      </div>
      <FieldDescription>Share this password with the user directly.</FieldDescription>
      <FieldError>{controller.errors.temporaryPassword}</FieldError>
    </Field>
  );
}

function AccessFields({
  controller,
  workspace,
}: {
  controller: Controller;
  workspace: WorkspaceAdminResponse;
}) {
  if (controller.role !== "member") {
    return (
      <Alert>
        <LockKeyholeIcon aria-hidden="true" />
        <AlertDescription>
          Workspace administrators always have access to all {workspace.tools.length} tools.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <>
      <ToolSelection
        disabled={controller.mutation.isPending}
        selected={controller.toolAccess}
        tools={workspace.tools}
        onChange={(next) => {
          controller.setToolAccess(next);
          controller.setErrors((current) => {
            const updated = { ...current };
            delete updated.toolAccess;
            return updated;
          });
        }}
      />
      <FieldError>{controller.errors.toolAccess}</FieldError>
    </>
  );
}

function AddUserForm({
  controller,
  isOnline,
  workspace,
}: {
  controller: Controller;
  isOnline: boolean;
  workspace: WorkspaceAdminResponse;
}) {
  const mutationMessage =
    controller.mutation.error instanceof ApiClientError
      ? controller.mutation.error.message
      : controller.mutation.error
        ? "The user could not be added. Try again."
        : null;
  return (
    <form className="grid gap-5" onSubmit={controller.submit}>
      <AccountTypeTabs controller={controller} />
      {controller.accountType === "existing" ? (
        <Alert>
          <KeyRoundIcon aria-hidden="true" />
          <AlertDescription>
            Enter their current CCBox username. Their name and password will not change.
          </AlertDescription>
        </Alert>
      ) : null}
      <IdentityFields controller={controller} />
      <AccessFields controller={controller} workspace={workspace} />
      {mutationMessage ? (
        <Alert role="alert" variant="destructive">
          <AlertDescription>{mutationMessage}</AlertDescription>
        </Alert>
      ) : null}
      <DialogFooter>
        <DialogClose
          disabled={controller.mutation.isPending}
          render={<Button type="button" variant="ghost" />}
        >
          Cancel
        </DialogClose>
        <Button disabled={controller.mutation.isPending || !isOnline} type="submit">
          <UserPlusIcon aria-hidden="true" data-icon="inline-start" /> Add user
        </Button>
      </DialogFooter>
    </form>
  );
}

export function AddWorkspaceUserDialog({
  isOnline,
  organizationId,
  workspace,
  onCreated,
}: {
  isOnline: boolean;
  organizationId: string;
  workspace: WorkspaceAdminResponse;
  onCreated: (result: WorkspaceMemberCreated) => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const controller = useAddUserController({
    organizationId,
    onCreated,
    onClose: () => {
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen && !controller.mutation.isPending) controller.reset();
      }}
    >
      <DialogTrigger render={<Button type="button" />}>
        <UserPlusIcon aria-hidden="true" data-icon="inline-start" /> Add user
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a workspace user</DialogTitle>
          <DialogDescription>
            Create a sign-in or add someone who already uses CCBox.
          </DialogDescription>
        </DialogHeader>
        <AddUserForm controller={controller} isOnline={isOnline} workspace={workspace} />
      </DialogContent>
    </Dialog>
  );
}
